import React, {useEffect, useState} from 'react';
import {useLoaderData, useParams} from "react-router";
import {Template} from "../../../../components/template/template.jsx";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../../services/web/bos-service.js";
import {FaExternalLinkAlt} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import BoInvalidate from "../components/bo-invalidate.jsx";
import InputFile from "../../../../components/input/input-file.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import Button from "../../../../components/button/button.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import {toast} from "react-toastify";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const BoUploadOrStart = () => {
    const {id: idRaw} = useParams();
    const id = idRaw?.replace("-", "/");

    const navigate = navigateHook();

    const [isLoading, setIsLoading] = useState(false);
    const [file, setFile] = useState(undefined);
    const [pdfSrc, setPdfSrc] = useState(null);

    const loaderData = useLoaderData();

    const {data, refetch, isLoading: isFetchLoading} = useQuery({
        queryFn: () => bosService.getBoInfo(idRaw),
        queryKey: ["bo-info"],
        enabled: !!idRaw
    });

    const {data: statusHistory, refetch: historyRefetch} = useQuery({
        queryFn: () => bosService.getStatusHistoryByBoWeb(id),
        queryKey: ["bo-statusHistory"],
        enabled: !!idRaw
    });

    const {data: occurrenceFile} = useQuery({
        queryFn: () => bosService.getOccurrenceFile(id),
        queryKey: ["bo-occurrence-file"],
        enabled: !!data && !!data.has_occurrencefile
    });

    useEffect(() => {
        if (!occurrenceFile || occurrenceFile.size > 1048576) return;

        let objectUrl = null;

        const loadPdf = async () => {
            try {
                setIsLoading(true);

                const response = await fetch(API_KEEPER + "/ged/attach/print/pdf/" + occurrenceFile.attachId);
                const blob = await response.blob();

                objectUrl = URL.createObjectURL(blob);

                setPdfSrc(objectUrl);
            } finally {
                setIsLoading(false);
            }
        };

        loadPdf();

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [occurrenceFile]);

    const sendFile = () => {
        if (file == null) {
            projectUtils.swalErrorMessage('', 'É necessário selecionar um arquivo', 'Ok');
            return;
        }

        if (file.file.type !== 'application/pdf') {
            projectUtils.swalErrorMessage('', 'Somente arquivos PDF são permitidos', 'Ok');
            return;
        }

        projectUtils.swalMessage(
            'warning',
            'Enviar anexo',
            `Deseja continuar? <br> Essa ação não poderá ser desfeita.`,
            'Sim',
            true,
            'Não',
            ({isConfirmed}) => {
                if (isConfirmed) {
                    setIsLoading(true);

                    const formData = new FormData();
                    formData.append("bogcmFile", file.file);
                    formData.append("id", id);

                    bosService.uploadPdf(formData)
                        .then(() => {
                            refetch();
                            historyRefetch();
                        })
                        .catch(error => {
                            console.error(error);
                            let message = 'Não foi possível enviar o arquivo.';
                            if (error.response.status !== 500) {
                                message = error.response.data.message;
                            }

                            projectUtils.swalErrorMessage('', message, 'Ok');
                        })
                        .finally(() => setIsLoading(false));
                }
            },
            false,
            "warning",
            true
        );
    }

    useEffect(() => {
        if(id && loaderData?.bo_full_name) document.title = loaderData.bo_name + " - " + id
    }, [loaderData, id]);

    return (
        <Template isLoading={isLoading || isFetchLoading}>
            <Template.Header>
                <Template.Title className={"flex gap-2 items-center"}>
                    {loaderData?.bo_name} {id}
                    {
                        !!data?.dispatchId &&
                        <span className={"cursor-pointer"} onClick={() => navigate("/despacho/dispatch/form/" + data?.dispatchId, true)}>
                            <FaExternalLinkAlt size={16}/>
                        </span>
                    }
                </Template.Title>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className={"resume-content space-y-4"}>
                        {
                            data && !data?.has_occurrencefile && statusHistory?.actions?.edit &&
                            <section className={"flex gap-2 justify-center"}>
                                <InputFile
                                    file={file}
                                    setFile={setFile}
                                    removeFile={() => setFile(undefined)}
                                    title={"Anexar " + loaderData?.bo_name}
                                    titleWithFile={"Visualizar anexo"}
                                    accept={"application/pdf"}
                                />

                                <Button onClick={() => navigate("/bo/form/" + idRaw)}>
                                    Preencher {loaderData?.bo_name}
                                </Button>
                            </section>
                        }

                        {
                            occurrenceFile && occurrenceFile.size <= 1048576 &&
                            <embed
                                src={pdfSrc}
                                type="application/pdf"
                                width="100%"
                                style={{height: "calc(100vh - 190px)"}}
                            />
                        }

                        {
                            occurrenceFile &&  occurrenceFile.size > 1048576 &&
                            <h3 className="alert ms-3 p-2 text-center">
                                O arquivo é muito grande para ser exibido nessa tela
                                <br/>
                                <b>
                                    <a href={API_KEEPER + "/ged/attach/print/pdf/" + occurrenceFile.attachId} target="_blank" className={"underline"}>Clique aqui para visualizar</a>
                                </b>
                            </h3>
                        }

                        {
                            statusHistory?.history.filter(item => item?.dateTime).map((item, index) => {
                                if (item.status === "NUMBER_GENERATED") {
                                    return (
                                        <p key={"history-" + index}>Numeração gerada por <b>{item?.userName}</b> em <b>{item?.dateTime}</b></p>
                                    )
                                }
                                if (item.status === "INVALIDATED") {
                                    return (
                                        <section key={"history-" + index}>
                                            <p>Invalidado por <b>{item?.userName}</b> em <b>{item?.dateTime}</b></p>
                                            <p className={"ms-3"}>{item.observation}</p>
                                        </section>
                                    )
                                }
                            })
                        }
                    </Card.Body>
                </Card>

                {
                    (statusHistory?.actions?.invalidate || (data && !data?.has_occurrencefile)) &&
                    <Card>
                        <Card.Body className={"flex gap-2 justify-end"}>
                            <BoInvalidate
                                id={id}
                                boName={loaderData?.bo_name}
                                setIsLoading={setIsLoading}
                                actionsItems={statusHistory?.actions}
                                callBack={() => {
                                    toast.success("Invalidado com sucesso");

                                    refetch();
                                    historyRefetch();
                                }}
                            />
                            {
                                data && !data?.has_occurrencefile &&
                                <Button disabled={!file} onClick={sendFile}>
                                    Enviar anexo
                                </Button>
                            }
                        </Card.Body>
                    </Card>
                }
            </Template.Body>
        </Template>
    );
};

export default BoUploadOrStart;